import React, {useCallback, useEffect, useMemo, useState} from 'react'

import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext'
import {
    LexicalTypeaheadMenuPlugin,
    useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import {
    $createTextNode,
    $getSelection,
    $isRangeSelection,
    TextNode,
} from 'lexical'
import {EmojiOption} from "./option"
import emojiMenu from "./menu"

type Emoji = {
    emoji           : string
    description     : string
    category        : string
    aliases         : Array<string>
    tags            : Array<string>
    unicode_version : string
    ios_version     : string
    skin_tones?     : boolean
}

const MAX_EMOJI_SUGGESTION_COUNT = 30

export default function EmojiPickerPlugin() {
    const [editor]                      = useLexicalComposerContext()
    const [queryString, setQueryString] = useState<string | null>(null)
    const [emojis, setEmojis]           = useState<Array<Emoji>>([])

    useEffect(() => {
        // @ts-ignore
        import('../emoji-list.ts').then((file) => setEmojis(file.default))
    }, [])

    const emojiOptions = useMemo(
        () => emojis != null ? emojis.map(({emoji, aliases, tags}) =>
            new EmojiOption(aliases[0], emoji, {
                keywords: [...aliases, ...tags],
            }),
        ) : [],
        [emojis],
    )

    const checkForTriggerMatch = useBasicTypeaheadTriggerMatch(':', {
        minLength: 0,
    })

    const options: Array<EmojiOption> = useMemo(() => {
        return emojiOptions
            .filter((option: EmojiOption) => {
                return queryString != null
                    ? new RegExp(queryString, 'gi').exec(option.label) ||
                    option.keywords != null
                        ? option.keywords.some((keyword: string) =>
                            new RegExp(queryString, 'gi').exec(keyword),
                        )
                        : false
                    : emojiOptions
            })
            .slice(0, MAX_EMOJI_SUGGESTION_COUNT)
    }, [emojiOptions, queryString])

    const onSelectOption = useCallback(
        (
            selectedOption  : EmojiOption,
            nodeToRemove    : TextNode | null,
            closeMenu       : () => void,
        ) => {
            editor.update(() => {
                const selection = $getSelection()

                if (!$isRangeSelection(selection) || selectedOption == null) {
                    return
                }

                if (nodeToRemove) {
                    nodeToRemove.remove()
                }

                selection.insertNodes([$createTextNode(selectedOption.emoji)])

                closeMenu()
            })
        },
        [editor],
    )

    return (
        <LexicalTypeaheadMenuPlugin onQueryChange   = {setQueryString}
                                    onSelectOption  = {onSelectOption}
                                    triggerFn       = {checkForTriggerMatch}
                                    options         = {options}
                                    menuRenderFn    = {emojiMenu}/>
    )
}
